<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

defineProps({ error: { type: String, default: '' } });
const emit = defineEmits(['export']);
const { t } = useI18n();
const route = useRoute();

const dialogRef = ref(null);

const segments = useMapGetter('customViews/getContactCustomViews');
const appliedFilters = useMapGetter('contacts/getAppliedContactFilters');
const uiFlags = useMapGetter('contacts/getUIFlags');
const isExportingContact = computed(() => uiFlags.value.isExporting);

const activeSegmentId = computed(() => route.params.segmentId);
const activeSegment = computed(() =>
  activeSegmentId.value
    ? segments.value.find(view => view.id === Number(activeSegmentId.value))
    : undefined
);
const isSegmentUnavailable = computed(
  () => Boolean(activeSegmentId.value) && !activeSegment.value
);

const scopeDescription = computed(() => {
  if (activeSegment.value) {
    return t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.SCOPE_SEGMENT', {
      name: activeSegment.value.name,
    });
  }
  // SiteUp: na etiqueta, uma frase só. Antes caía em "todos os contatos" e
  // logo abaixo dizia "restrito à etiqueta", contradizendo a primeira linha.
  if (route.params.label) {
    return t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.SCOPE_LABEL', {
      name: route.params.label,
    });
  }
  if (Object.keys(appliedFilters.value).length > 0) {
    return t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.SCOPE_FILTERED');
  }
  return t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.SCOPE_ALL');
});

const exportContacts = async () => {
  if (isExportingContact.value || isSegmentUnavailable.value) return;
  let query = { payload: [] };

  if (activeSegmentId.value && activeSegment.value) {
    query = activeSegment.value.query;
  } else if (Object.keys(appliedFilters.value).length > 0) {
    query = filterQueryGenerator(appliedFilters.value);
  }

  emit('export', {
    ...query,
    label: route.params.label || '',
  });
};

const requestClose = () => {
  if (!isExportingContact.value) dialogRef.value?.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.TITLE')"
    :description="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.DESCRIPTION')
    "
    :confirm-button-label="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.CONFIRM')
    "
    :is-loading="isExportingContact"
    :disable-confirm-button="isExportingContact || isSegmentUnavailable"
    :show-cancel-button="!isExportingContact"
    confirm-before-close
    @request-close="requestClose"
    @confirm="exportContacts"
  >
    <div
      v-if="!isSegmentUnavailable"
      class="rounded-xl border border-n-weak bg-n-alpha-1 p-3 text-sm text-n-slate-12"
    >
      <p class="m-0">{{ scopeDescription }}</p>
    </div>
    <p
      v-if="isSegmentUnavailable"
      role="alert"
      class="m-0 text-sm text-n-ruby-11"
    >
      {{ t('FILTER.SAVED_LOAD_ERROR') }}
    </p>
    <p v-if="error" role="alert" class="m-0 text-sm text-n-ruby-11">
      {{ error }}
    </p>
  </Dialog>
</template>
